<template>
    <ValidationErrors :show="showErrors"/>
    <Head title="Create New Management Fee Invoice"/>
    <div class="w-full mb-6">
        <div class="flex justify-between">
            <h1 class="my-2 text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                Create Management Fee Invoice <span class="text-sm text-gray-500">for {{ organisation.name }}</span>
            </h1>
        </div>
        <div class="space-y-6 lg:col-span-9">
            <section aria-labelledby="invoice-information-heading">
                <form @submit.prevent="submit">
                    <div class="shadow sm:rounded-md sm:overflow-hidden">
                        <div class="bg-white px-4 p-6 space-y-6">
                            <div class="space-y-6">
                                <div class="border-b border-gray-200 pb-2">
                                    <h2 id="payment-details-heading" class="text-lg leading-6 font-bold text-gray-900">
                                        Invoice Information
                                    </h2>
                                    <p class="mt-1 text-sm text-gray-500">
                                        Enter information for the invoice.
                                    </p>
                                    <p class="mt-2 w-full text-right text-xs text-gray-500 font-italic">
                                        Fields marked with an asterisk (<span class="text-red-500">*</span>) are required.
                                    </p>
                                </div>
                                <div v-if="clients" class="col-span-4 clear-both">
                                    <div class="rounded-md bg-blue-50 p-4 mt-2 mb-5">
                                        <div class="flex">
                                            <div class="flex-shrink-0">
                                                <InformationCircleIcon class="h-5 w-5 text-blue-400" aria-hidden="true"/>
                                            </div>
                                            <div class="ml-3 flex-1 md:flex md:justify-between">
                                                <p class="text-sm text-blue-700">
                                                    Select additional clients linked to this client's group below.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="col-span-4 clear-both">
                                        <div v-for="(client, index) in clients" :key="client.name" class="flex items-center pb-3">
                                            <label :for="'additional_clients' + client.id" class="flex items-center space-x-2">
                                                <input type="checkbox" :id="'additional_clients' + client.id"
                                                    class="w-4 h-4 rounded border-gray-300 text-pitch-purple focus:ring-indigo-500 sm:left-6"
                                                    :value="client.id"
                                                    @change="clientSelected(client.id)"
                                                />
                                                <span class="block text-sm font-medium text-gray-700 pl-2">{{ client.name }} {{ client.reference }}</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <div class="grid grid-cols-4 gap-6">
                                    <div class="col-span-3 sm:col-span-2 lg:col-span-1">
                                        <label for="reference" class="block text-sm font-medium text-gray-700 required">
                                            Reference
                                        </label>
                                        <input type="text" v-model="form.reference" name="reference" id="reference"
                                               autocomplete="reference"
                                               class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>
                                    <div class="col-span-3 sm:col-span-2 lg:col-span-1">
                                        <label for="po_reference" class="block text-sm font-medium text-gray-700 required">
                                            PO Reference
                                        </label>
                                        <input type="text" v-model="form.po_reference" name="po_reference" id="po_reference"
                                               autocomplete="po_reference"
                                               class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>
                                    <div class="col-span-3 sm:col-span-2 lg:col-span-1">
                                        <label for="month" class="block text-sm font-medium text-gray-700 required">
                                            Month
                                        </label>
                                        <select id="months" v-model="form.month" class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                            <option value="January">January</option>
                                            <option value="February">February</option>
                                            <option value="March">March</option>
                                            <option value="April">April</option>
                                            <option value="May">May</option>
                                            <option value="June">June</option>
                                            <option value="July">July</option>
                                            <option value="August">August</option>
                                            <option value="September">September</option>
                                            <option value="October">October</option>
                                            <option value="November">November</option>
                                            <option value="December">December</option>
                                        </select>
                                    </div>
                                    <div class="col-span-3 sm:col-span-2 lg:col-span-1">
                                        <label for="year" class="block text-sm font-medium text-gray-700 required">
                                            Year
                                        </label>
                                        <select id="year" v-model="form.year"
                                                class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                            <option v-for="year in years" :key="year">{{ year }}</option>
                                        </select>
                                    </div>
                                </div>
                                <div class="grid grid-cols-3 gap-6">
                                    <div class="col-span-3 sm:col-span-2 lg:col-span-1">
                                        <label for="amount" class="block text-sm font-medium text-gray-700 required">
                                            Amount
                                        </label>
                                        <input type="number" v-model="form.amount" name="amount" id="amount"
                                               autocomplete="amount" step="0.01" disabled
                                               class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>
                                    <div class="col-span-1">
                                        <label for="invoiced_at" class="block text-sm font-medium text-gray-700 required">
                                            Invoice Date <span v-if="formattedInvoiceDate">({{ formattedInvoiceDate }})</span>
                                        </label>
                                        <input type="date" v-model="form.invoiced_at" name="invoiced_at" id="invoiced_at"
                                               autocomplete="amount"
                                               class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>
                                    <div class="col-span-3 sm:col-span-2 lg:col-span-1">
                                        <label for="due_at" class="block text-sm font-medium text-gray-700 required">
                                            Due Date <span v-if="formattedDueDate">({{ formattedDueDate }})</span>
                                        </label>
                                        <input type="date" v-model="form.due_at" name="due_at" id="due_at"
                                               autocomplete="due_at"
                                               class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>
                                </div>
                                <div class="grid grid-cols-4 gap-6">
                                    <div class="col-span-4 clear-both">
                                        <label class="block text-sm font-medium text-gray-700">
                                            Payment Instructions
                                        </label>
                                        <div class="mt-1">
                                            <quill-editor content-type="html" v-model:content="form.payment_instructions"
                                                          class="min-h-[150px] block w-full bg-white border border-gray-300 shadow-sm py-2 px-3"
                                                          theme="snow">
                                            </quill-editor>
                                        </div>
                                    </div>
                                    <div class="col-span-4 clear-both">
                                        <label class="block text-sm font-medium text-gray-700">
                                            Additional Notes
                                        </label>
                                        <div class="mt-1">
                                            <quill-editor content-type="html" v-model:content="form.additional_notes"
                                                          class="min-h-[150px] block w-full bg-white border border-gray-300 shadow-sm py-2 px-3"
                                                          theme="snow">
                                            </quill-editor>
                                        </div>
                                    </div>
                                </div>
                                <div class="w-full border">
                                    <div class="flex-grow overflow-auto">
                                        <div class="relative w-full">
                                            <table class="min-w-full divide-y divide-gray-200 overflow-x">
                                                <thead class="bg-gray-50">
                                                    <tr>
                                                        <th scope="col" class="min-w-[7rem] py-3.5 pl-5 text-left text-sm font-semibold text-gray-900">
                                                            <div class="absolute inset-y-0 left-0 w-0.5 bg-pitch-purple"></div>
                                                            <div class="flex items-center pl-1">
                                                                <input
                                                                    id="toggleSelectAll"
                                                                    type="checkbox"
                                                                    class="w-4 h-4 rounded border-gray-300 text-pitch-purple focus:ring-indigo-500 sm:left-6"
                                                                    @click="toggleSelectAll"
                                                                />
                                                                <label for="toggleSelectAll" class="ml-2 text-sm font-medium text-black">
                                                                    <span v-if="!this.selectAll">Select All</span>
                                                                    <span v-else>Deselect All</span>
                                                                </label>
                                                            </div>
                                                        </th>
                                                        <th scope="col"
                                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                            Fixture Reference
                                                        </th>
                                                        <th scope="col"
                                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                            Fixure Title
                                                        </th>
                                                        <th scope="col"
                                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                            Client
                                                        </th>
                                                        <th scope="col"
                                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                            Team Manager
                                                        </th>
                                                        <th scope="col"
                                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                            Invoice Reference
                                                        </th>
                                                        <th scope="col"
                                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                            Invoice Amount
                                                        </th>
                                                        <th scope="col"
                                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                            Invoice Date
                                                        </th>
                                                        <th scope="col"
                                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                            Management Fee Percentage
                                                        </th>
                                                        <th scope="col"
                                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                            Management Fee Amount
                                                        </th>
                                                        <th scope="col"
                                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr v-if="this.invoices.length > 0" v-for="(invoice, index) in this.invoices" :key="index">
                                                        <td class="relative w-12 px-6 sm:w-16 sm:px-8">
                                                            <input
                                                                type="checkbox"
                                                                class="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-pitch-purple focus:ring-indigo-500 sm:left-6"
                                                                :value="invoice.id"
                                                                @change="toggleSelection(invoice)"
                                                                :checked="selectedInvoiceIds.includes(invoice.id)"
                                                                />
                                                        </td>
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                            {{ invoice.fixture_reference }}
                                                        </td>
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                            {{ invoice.fixture_title }}
                                                            <br/>
                                                            (Quote: {{ invoice.quote_name }})
                                                        </td>
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                            {{ invoice.client_name }}
                                                        </td>
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                            {{ invoice.travel_manager_first_name + ' ' + invoice.travel_manager_last_name }}
                                                        </td>
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                            {{ invoice.invoice_reference }}
                                                        </td>
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                            {{ formatCurrency(invoice.invoice_amount, invoice.currency_code, true) }}
                                                        </td>
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                            {{ moment(invoice.invoiced_at, 'YYYY-MM-DD').format('DD MMM YYYY') }}
                                                        </td>
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                            {{ invoice.management_fee_percentage }}%
                                                        </td>
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                            {{ formatCurrency(invoice.management_fee_amount, invoice.currency_code, true) }}
                                                        </td>
                                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                            <a class="flex gap-1 items-center cursor-pointer hover:underline" :href="downloadInvoice(invoice)" title="Download Invoice"><DocumentDownloadIcon class="w-4 h-4" /></a>
                                                        </td>
                                                    </tr>
                                                    <tr v-if="this.selectedInvoiceIds.length > 0">
                                                        <td colspan="8" class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                                                            <a :href="route('frontend.management-fees.export-management-fee-invoice-list', {
                                                                _query: {
                                                                    selected_clients: this.selectedClients,
                                                                    po_reference: this.form.po_reference,
                                                                    month: this.form.month,
                                                                    year: this.form.year,
                                                                    invoice_date: this.form.invoiced_at,
                                                                    due_date: this.form.due_at,
                                                                    payment_instructions: this.form.payment_instructions,
                                                                    additional_notes: this.form.additional_notes,
                                                                    invoice_ids: this.selectedInvoiceIds
                                                                }
                                                            })"
                                                            class="ml-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm rounded-md text-white bg-pitch-purple hover:bg-dark-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-200 cursor-pointer">
                                                                Export Invoice List
                                                            </a>
                                                        </td>
                                                    </tr>
                                                    <tr v-else>
                                                        <td colspan="8" class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                            There are no management fee invoices available for your selected clients.
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="px-4 py-3 bg-gray-50 text-right sm:px-6">
                            <div class="flex-shrink-0 px-4 py-4 flex justify-end">
                                <button :disabled="form.processing || !form.isDirty" type="button"
                                        class=" ml-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm rounded-md text-white bg-green-500 hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-200"
                                        @click="previewInvoice()">
                                    <span>Preview</span>
                                </button>
                                <button :disabled="form.processing || !form.isDirty" type="submit"
                                        class="ml-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm rounded-md text-white bg-pitch-purple hover:bg-dark-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-200">
                                    <span>Save</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </section>
        </div>
    </div>
</template>

<script>
import Authenticated from "@/Layouts/Authenticated";
import {ref} from "vue";
import {Link, Head, usePage, useForm} from "@inertiajs/vue3";
import {InformationCircleIcon, DocumentDownloadIcon} from "@heroicons/vue/outline"
import {CreditCardIcon, NewspaperIcon} from "@heroicons/vue/outline";
import ValidationErrors from "@/Components/ValidationErrors";
import axios from 'axios';
import moment from "moment";

export default {
    layout: Authenticated,
    components: {
        Head,
        Link,
        CreditCardIcon,
        NewspaperIcon,
        ValidationErrors,
        InformationCircleIcon,
        DocumentDownloadIcon
    },
    setup (props) {
        const organisation = usePage().props.organisation ?? null;
        const clients = usePage().props.clients ?? null;
        const reference = usePage().props.reference ?? null;
        const invoiced_at = usePage().props.invoiced_at ?? null;
        const due_at = usePage().props.due_at ?? null;
        const years = usePage().props.years ?? null;
        const showErrors = ref(false);
        const invoices = ref([]);
        const loading = ref(null);

        const months = [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];

        const current_month = months[new Date().getMonth()];

        const current_year = new Date().getFullYear();

        const form = useForm({
            id: null,
            quote_service_ids: [],
            related_invoice_ids: [],
            reference: reference,
            po_reference: null,
            month: current_month,
            year: current_year,
            amount: null,
            invoiced_at: invoiced_at,
            due_at: due_at,
            payment_instructions: null,
            additional_notes: null
        });

        return {
            organisation,
            clients,
            form,
            showErrors,
            selectedClients: [],
            invoices,
            selectedInvoiceIds: [],
            selectAll: false,
            selectAllClients: false,
            loading,
            years
        }
    },
    created() {
        this.moment = moment
    },
    computed: {
        formattedInvoiceDate() {
            if (this.form.invoiced_at) {
                return moment(this.form.invoiced_at).format('DD MMM YYYY');
            }
            return '';
        },
        formattedDueDate() {
            if (this.form.due_at) {
                return moment(this.form.due_at).format('DD MMM YYYY');
            }
            return '';
        }
    },
    methods: {
        submit() {
            // Perform the post
            this.form.post(route('frontend.management-fees.organisation.store', [this.organisation.id]), {
                onSuccess: page => {
                    this.show = false;
                    this.showErrors = false;
                    this.form.reset();
                    this.selectedLines = [];
                },
                onError: errors => {
                    this.showErrors = true;
                    if (errors.reference) {
                        this.getConfig();
                    }
                }
            });
        },
        clientSelected(clientId) {
            if(this.selectedClients.includes(clientId)) {
                // Instantiate index
                const index = this.selectedClients.indexOf(clientId);

                // Remove item from selected clients
                if (index > -1) {
                    this.selectedClients.splice(index, 1);
                }

                // Fetch the service lines
                this.fetchServiceLines(this.selectedClients, this.form.month, this.form.year);
            } else {
                // Add item to selected clients
                this.selectedClients.push(clientId);

                // Fetch the service lines
                this.fetchServiceLines(this.selectedClients, this.form.month, this.form.year);
            }
        },
        fetchServiceLines(selectedClients, month, year) {
            if(selectedClients.length == 0) {
                // Reset the invoices without making the request if the selected clients is empty
                this.invoices = [];
                this.selectedInvoiceIds = [];
            } else {
                axios.get(route('frontend.management-fees.get-service-lines'), {
                    params: {
                        selected_client_ids: selectedClients,
                        month: month,
                        year: year
                    }
                }).then(response => {
                    this.selectedInvoiceIds = [];
                    this.invoices = response.data.invoices;
                });
            }
        },
        toggleSelectAll() {
            if (!this.selectAll) {
                // Select all
                this.selectedInvoiceIds = this.invoices?.map(line => line.id);
                this.selectAll = true;
                this.form.related_invoice_ids = this.selectedInvoiceIds;
                this.form.amount = this.invoices.reduce((total, invoice) => total + invoice.management_fee_amount, 0);
            } else {
                // Deselect all
                this.selectedInvoiceIds = [];
                this.selectAll = false;
                this.form.related_invoice_ids = this.selectedInvoiceIds;
                this.form.amount = 0;
            }

            this.form.amount = parseFloat(this.form.amount.toFixed(2));
        },
        toggleSelectAllClients() {
            if (!this.selectAllClients) {
                // Map the client ids to the selected clients
                this.selectedClients = this.clients.map(client => client.id);

                // Set select all to be true
                this.selectAllClients = true;

                // Fetch the service lines
                this.fetchServiceLines(this.selectedClients);
            } else {
                // Reset all associated variables                
                this.selectedClients = [];
                
                // Set select all to be false
                this.selectAllClients = false;

                // Fetch the service lines
                this.fetchServiceLines(this.selectedClients);
            }
        },
        toggleSelection(invoice) {
            if (this.selectedInvoiceIds.includes(invoice.id)) {
                const index = this.selectedInvoiceIds.indexOf(invoice.id);
                this.selectedInvoiceIds.splice(index, 1);
                this.form.related_invoice_ids = this.selectedInvoiceIds;
                this.form.amount -= invoice.management_fee_amount;
            } else {
                this.selectedInvoiceIds.push(invoice.id);
                this.form.related_invoice_ids = this.selectedInvoiceIds;
                this.form.amount += invoice.management_fee_amount;
            }

            this.form.amount = parseFloat(this.form.amount.toFixed(2));
        },
        filterInvoiceList() {
            this.fetchServiceLines(this.selectedClients, this.form.month, this.form.year);
        },
        previewInvoice() {
            axios({
                url: route('frontend.management-fees.organisation.preview', [this.organisation.id]),
                method: "POST",
                data: this.form,
                responseType: 'arraybuffer'
            }).then(r => {
                const path = window.URL.createObjectURL(new Blob([r.data]));
                const link = document.createElement('a');
                link.href = path;
                link.setAttribute('download', 'DRAFT-' + this.form.reference + '.pdf');
                document.body.appendChild(link);
                link.click();
            });
        },
        downloadInvoice (invoice) {
            var url = '';

            url = route('frontend.invoices.download', [invoice.id]);

            return url;
        }
    },
    watch: {
        selectedInvoiceIds: function(newIds, oldIds) {
            // Check if all invoices are selected
            this.selectAll = newIds.length === this.invoices.length;

            // Call toggleSelection with the changed selectedInvoiceIds
            this.toggleSelection();
        },
        'form.month': function() {
            this.filterInvoiceList();
        },
        'form.year': function() {
            this.filterInvoiceList();
        }
    }
}
</script>
